import LPressable from "@components/LPressable";
import { useEffect, useRef, useState } from "react";
import {
  ActivityIndicator,
  AppState,
  Platform,
  StyleSheet,
  Text,
  View,
} from "react-native";
import type { TextInput, TextStyle, ViewStyle } from "react-native";
import Animated from "react-native-reanimated";
import type { AnimatedStyle } from "react-native-reanimated";
import Svg, { Circle, Path } from "react-native-svg";

import { CodeSlots } from "./CodeSlots";
import type { CodeSlotsLook } from "./CodeSlots";
import { PasteCode } from "./PasteCode";

export const CODE_LENGTH = 6;

/** Where the step is: waiting for a code, checking one, or done with it. */
export type CodeStepStatus = "idle" | "checking" | "verified";

export type CodeStepLook = CodeSlotsLook & {
  secondary: string;
  /** The host's action colour (resend, paste), and what is written on it. */
  action: string;
  onAction: string;
  successText: string;
  chipFill: string;
  chipBorder: string;
  /** Between the step's rows. */
  gap: number;
  /** The host's own typeface; the system's when it sets none. */
  fonts?: { medium: string; semiBold: string };
};

type Box = { x: number; y: number; width: number; height: number };

/** Where the step's pieces are, in the step's own coordinates, for a host
 *  that draws its arrival. */
export type CodeStepGeometry = {
  height: number;
  slots: Box;
  /** The address inside the chip. */
  address: Box;
};

/** A host that draws the step's arrival drives its pieces with these. */
export type CodeStepMotion = {
  label?: AnimatedStyle<TextStyle>;
  chip?: AnimatedStyle<ViewStyle>;
  address?: AnimatedStyle<TextStyle>;
  slots?: AnimatedStyle<ViewStyle>;
  status?: AnimatedStyle<ViewStyle>;
};

/**
 * The one-time code step, as Link in Bio's contact step draws it: where the
 * code went (× to change it), six slots that are checked at the sixth digit,
 * and one line under them saying what is happening — sent and when another
 * may be, being checked, accepted. A rejected code clears for the next try
 * with the keyboard still up. "Paste code" fills the slots in one tap.
 *
 * The host owns the code's journey (`onCode`, `status`, `error`); the step
 * owns what is typed.
 */
export function CodeStep({
  email,
  status,
  verifiedText,
  error,
  resendAt,
  onCode,
  onInput,
  onResend,
  onEdit,
  look,
  ids,
  events,
  motion,
  onGeometry,
}: {
  email: string;
  status: CodeStepStatus;
  /** What the line under the slots says once the code is accepted. */
  verifiedText: string;
  error: string | null;
  /** When another code may be asked for (epoch ms). */
  resendAt: number;
  /** Six digits are in: the host checks them. */
  onCode: (code: string) => void;
  /** The fan is typing again: the host drops its error. */
  onInput?: () => void;
  onResend: () => Promise<unknown>;
  /** × on the chip: back to the address. */
  onEdit: () => void;
  look: CodeStepLook;
  /** testID prefix: the step, `-input`, `-change-email`, `-resend`, `-paste`. */
  ids: string;
  events: { changeEmail: string; resend: string; paste: string };
  motion?: CodeStepMotion;
  onGeometry?: (geometry: CodeStepGeometry) => void;
}) {
  const input = useRef<TextInput>(null);
  const [code, setCode] = useState("");
  const [resending, setResending] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const secondsLeft = Math.max(0, Math.ceil((resendAt - now) / 1000));
  const verified = status === "verified";
  const idle = status === "idle" && !resending;

  const counting = secondsLeft > 0;
  useEffect(() => {
    if (!counting) return;
    const timer = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(timer);
  }, [counting, resendAt]);

  // A code that was checked and not accepted: cleared for the next try. The
  // keyboard goes once one is accepted.
  const lastStatus = useRef(status);
  useEffect(() => {
    if (lastStatus.current !== "idle" && status === "idle") {
      setCode("");
      input.current?.focus();
    }
    if (status === "verified") input.current?.blur();
    lastStatus.current = status;
  }, [status]);

  // Back from the mail app: the keyboard returns without a tap.
  useEffect(() => {
    if (status !== "idle") return;
    const subscription = AppState.addEventListener("change", (state) => {
      if (state === "active") {
        setNow(Date.now());
        input.current?.focus();
      }
    });
    return () => subscription.remove();
  }, [status]);

  function take(next: string) {
    // A clipboard read can land while a code is being checked, or after one
    // was accepted.
    if (!idle) return;
    setCode(next);
    onInput?.();
    if (next.length === CODE_LENGTH) onCode(next);
  }

  const boxes = useRef<{
    height?: number;
    head?: Box;
    chip?: Box;
    address?: Box;
    slots?: Box;
  }>({});
  function measured() {
    const { height, head, chip, address, slots } = boxes.current;
    if (!onGeometry || !height || !head || !chip || !address || !slots) return;
    onGeometry({
      height,
      slots,
      address: {
        ...address,
        x: head.x + chip.x + address.x,
        y: head.y + chip.y + address.y,
      },
    });
  }

  const font = (weight: "medium" | "semiBold"): TextStyle =>
    look.fonts
      ? { fontFamily: look.fonts[weight] }
      : { fontWeight: weight === "medium" ? "500" : "600" };
  const lineText = [styles.lineText, font("medium"), { color: look.secondary }];

  return (
    <View
      onLayout={(event) => {
        boxes.current.height = event.nativeEvent.layout.height;
        measured();
      }}
      style={{ gap: look.gap }}
      testID={ids}
    >
      <View
        onLayout={(event) => {
          boxes.current.head = event.nativeEvent.layout;
          measured();
        }}
        style={styles.head}
      >
        <Animated.Text
          style={[
            styles.title,
            font("semiBold"),
            { color: look.text },
            motion?.label,
          ]}
        >
          Enter the code sent to
        </Animated.Text>
        <Animated.View
          onLayout={(event) => {
            boxes.current.chip = event.nativeEvent.layout;
            measured();
          }}
          style={[
            styles.chip,
            { backgroundColor: look.chipFill, borderColor: look.chipBorder },
            motion?.chip,
          ]}
        >
          <Animated.Text
            ellipsizeMode="middle"
            numberOfLines={1}
            onLayout={(event) => {
              boxes.current.address = event.nativeEvent.layout;
              measured();
            }}
            style={[
              styles.chipText,
              font("medium"),
              { color: look.text },
              motion?.address,
            ]}
          >
            {email}
          </Animated.Text>
          {verified ? null : (
            <LPressable
              accessibilityLabel="Change email"
              accessibilityRole="button"
              disabled={!idle}
              hitSlop={8}
              onPress={onEdit}
              sentryEventName={events.changeEmail}
              shouldDismissKeyboard={false}
              style={styles.remove}
              testID={`${ids}-change-email`}
            >
              <Svg height={10} viewBox="0 0 10 10" width={10}>
                <Path
                  d="M2 2l6 6M8 2l-6 6"
                  stroke={look.secondary}
                  strokeLinecap="round"
                  strokeWidth={1.6}
                />
              </Svg>
            </LPressable>
          )}
        </Animated.View>
      </View>
      <CodeSlots
        accepted={verified}
        autoFocus
        busy={!idle}
        dimWhenBusy={status === "idle"}
        drawnStyle={motion?.slots}
        inputRef={input}
        // An accepted code outranks an earlier attempt's error.
        invalid={Boolean(error) && !verified}
        look={look}
        onChange={take}
        onLayout={(event) => {
          boxes.current.slots = event.nativeEvent.layout;
          measured();
        }}
        testID={`${ids}-input`}
        value={code}
      />
      {error && !verified ? (
        <Text
          accessibilityRole="alert"
          style={[styles.error, font("medium"), { color: look.danger }]}
        >
          {error}
        </Text>
      ) : null}
      <Animated.View
        accessibilityLiveRegion="polite"
        style={[styles.line, motion?.status]}
      >
        {verified ? (
          <>
            <Spinner color={look.success} />
            <Text
              accessibilityRole="text"
              style={[
                styles.lineText,
                font("semiBold"),
                { color: look.successText },
              ]}
            >
              {verifiedText}
            </Text>
          </>
        ) : status === "checking" ? (
          <>
            <Spinner color={look.text} />
            <Text accessibilityRole="text" style={lineText}>
              Checking your code…
            </Text>
          </>
        ) : (
          <>
            <Tick color={look.success} />
            {secondsLeft > 0 ? (
              <Text style={lineText}>
                Code sent · resend in {secondsLeft} s
              </Text>
            ) : (
              <View style={styles.resendLine}>
                <Text style={lineText}>Code sent ·</Text>
                <LPressable
                  accessibilityLabel="Resend code"
                  accessibilityRole="button"
                  disabled={!idle}
                  hitSlop={8}
                  onPointerDown={(event) => {
                    if (Platform.OS === "web") event.preventDefault();
                  }}
                  onPress={() => {
                    if (!idle) return;
                    setResending(true);
                    setCode("");
                    void onResend()
                      .then(
                        () => setNow(Date.now()),
                        () => undefined,
                      )
                      .finally(() => setResending(false));
                  }}
                  sentryEventName={events.resend}
                  shouldDismissKeyboard={false}
                  testID={`${ids}-resend`}
                >
                  <Text
                    style={[
                      styles.lineText,
                      font("semiBold"),
                      { color: look.action },
                    ]}
                  >
                    resend
                  </Text>
                </LPressable>
              </View>
            )}
            <View style={styles.paste}>
              <PasteCode
                color={look.action}
                disabled={!idle}
                fontFamily={look.fonts?.semiBold}
                onCode={(pasted) => {
                  if (pasted) take(pasted);
                  // Nothing to paste, or a short code: back to typing.
                  if (pasted.length < CODE_LENGTH) input.current?.focus();
                }}
                onColor={look.onAction}
                sentryEventName={events.paste}
                testID={`${ids}-paste`}
              />
            </View>
          </>
        )}
      </Animated.View>
    </View>
  );
}

/** The step's own spinner, small. */
const Spinner = ({ color }: { color: string }) => (
  <ActivityIndicator color={color} size="small" style={styles.spinner} />
);

const Tick = ({ color }: { color: string }) => (
  <Svg height={14} viewBox="0 0 14 14" width={14}>
    <Circle cx={7} cy={7} fill={color} r={7} />
    <Path
      d="M4.2 7.3l1.9 1.8 3.7-4"
      fill="none"
      stroke="#fff"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.6}
    />
  </Svg>
);

const styles = StyleSheet.create({
  head: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    justifyContent: "space-between",
  },
  title: { fontSize: 14 },
  chip: {
    alignItems: "center",
    borderRadius: 999,
    borderWidth: 1,
    flexDirection: "row",
    flexShrink: 1,
    gap: 6,
    minHeight: 28,
    paddingLeft: 11,
    paddingRight: 7,
  },
  chipText: { flexShrink: 1, fontSize: 13 },
  remove: {
    alignItems: "center",
    height: 18,
    justifyContent: "center",
    width: 18,
  },
  error: { fontSize: 13 },
  // As tall as the system's paste button, so the line holds its height
  // whichever state it is in.
  line: { alignItems: "center", flexDirection: "row", gap: 6, minHeight: 34 },
  lineText: { flexShrink: 1, fontSize: 12.5 },
  resendLine: {
    alignItems: "center",
    flexDirection: "row",
    gap: 4,
    flexShrink: 1,
  },
  paste: { marginLeft: "auto" },
  spinner: { height: 14, transform: [{ scale: 0.7 }], width: 14 },
});
